import * as _ from 'lodash-es'
import React from 'react'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import {
  ActionAddIcon,
  ActionCodeIcon,
  ActionDollarIcon,
  ActionIdBadgeIcon,
  ActionMemoCheckIcon,
  ActionMileageIcon,
  ActionMoreIcon,
  ActionPaperCheckIcon,
  ActionPaperIcon,
  ActionPersonIcon,
  ActionUploadIcon,
  CheckCircleIcon,
  IconClock,
  IconReceiptDollar,
} from '@/components/icons'

/**
 * Configures the icon for the specified `icon` string and `completed` status.
 * @param {string} icon
 * @param {boolean} completed
 * @returns icon component
 */
export const configureActionItemIcon = (icon, completed = false) => {
  switch (icon) {
    case 'paper-check':
      return (
        <ActionPaperCheckIcon
          className={mergeClassNames('stroke-blue h-6 w-6', completed && 'stroke-green')}
        />
      )
    case 'code':
      return (
        <ActionCodeIcon
          className={mergeClassNames('stroke-blue h-6 w-6', completed && 'stroke-green')}
        />
      )
    case 'upload':
      return (
        <ActionUploadIcon
          className={mergeClassNames('stroke-blue h-6 w-6', completed && 'stroke-green')}
        />
      )
    case 'person':
      return (
        <ActionPersonIcon
          className={mergeClassNames('stroke-blue h-6 w-6', completed && 'stroke-green')}
        />
      )
    case 'memo-check':
      return (
        <ActionMemoCheckIcon
          className={mergeClassNames('stroke-blue h-6 w-6', completed && 'stroke-green')}
        />
      )
    case 'mileage':
      return (
        <ActionMileageIcon
          className={mergeClassNames('stroke-blue h-6 w-6', completed && 'stroke-green')}
        />
      )
    case 'add':
      return (
        <ActionAddIcon
          className={mergeClassNames('stroke-blue h-6 w-6', completed && 'stroke-green')}
        />
      )
    case 'paper':
      return (
        <ActionPaperIcon
          className={mergeClassNames('stroke-blue h-6 w-6', completed && 'stroke-green')}
        />
      )
    case 'dollar':
      return (
        <ActionDollarIcon
          className={mergeClassNames('stroke-blue h-6 w-6', completed && 'stroke-green')}
        />
      )
    case 'id-badge':
      return (
        <ActionIdBadgeIcon
          className={mergeClassNames('stroke-blue h-6 w-6', completed && 'stroke-green')}
        />
      )
    case 'clock':
      return <IconClock size={24} strokeWidth={2} color={completed ? '#358500' : '#3697d3'} />
    case 'receipt':
      return (
        <IconReceiptDollar size={24} strokeWidth={2} color={completed ? '#358500' : '#3697d3'} />
      )
    // `more` doubles as the fallback. The slug now comes from the database, so
    // an icon added on the backend before this switch knows about it must still
    // draw something rather than leaving the action with no icon at all.
    case 'more':
    default:
      return (
        <ActionMoreIcon
          className={mergeClassNames('stroke-blue h-6 w-6', completed && 'stroke-green')}
        />
      )
  }
}

/**
 * Renders the line item.
 * @param {object} lineItem
 */
export const configureRepairOrderLineItem = (lineItem) =>
  lineItem.actions.length > 0 && (
    <li key={lineItem.id} className="flex flex-col gap-1">
      <p className="text-charcoal whitespace-nowrap pb-1 text-xs font-semibold uppercase">
        {lineItem.name}
      </p>

      {_.map(lineItem.actions, (a) => configureRepairOrderAction(a))}
    </li>
  )

/**
 * Renders the action item.
 * @param {object} action
 */
export const configureRepairOrderAction = (action) => (
  <li key={action.id} className="flex items-center">
    <div className="flex h-5 w-4 items-center overflow-hidden">
      {action.completed ? (
        <CheckCircleIcon className="h-4 w-4 text-green-700" />
      ) : (
        <div
          aria-hidden="true"
          className="border-brownGray-200 ml-[2px] h-3 w-3 rounded-full border-[1.25px]"
        />
      )}
    </div>

    <span className="text-charcoal-900 flex-1 pl-1">{action.name}</span>
  </li>
)
